// src/pages/Jobs.js
"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";

// If you use Appwrite, keep these:
import { databases } from "../appwriteConfig";
import { Query } from "appwrite";

/* ---------------- Small UI ---------------- */

const SectionWrap = ({ children }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
);

const Badge = ({ children, tone = "slate" }) => {
  const map = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    green: "border-green-200 bg-green-50 text-green-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium ${map[tone]}`}
    >
      {children}
    </span>
  );
};

const Button = ({ children, href = "#", className = "" }) => (
  <a
    href={href}
    className={`inline-flex items-center justify-center rounded-xl h-10 px-4 text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition ${className}`}
  >
    {children}
  </a>
);

const GhostButton = ({ children, href = "#", className = "" }) => (
  <a
    href={href}
    className={`inline-flex items-center justify-center rounded-xl h-10 px-4 text-sm font-semibold border border-slate-300 text-slate-900 hover:bg-slate-50 transition ${className}`}
  >
    {children}
  </a>
);

/* --------------- Job Card ---------------- */

const JobCard = ({ job }) => {
  const { title, trade, location, province, postedAt } = job || {};

  const subject = encodeURIComponent(`Application: ${title} — ${location}, ${province}`);
  const body = encodeURIComponent(
    `Hi,\n\nI’d like to be considered for the role: ${title} (${location}, ${province}).\n\nAttached is my resume.\n\nThanks,\n[Your Name]\n[Phone]\n[LinkedIn]`
  );

  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition">
      <div className="p-5 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug">
            {title}
          </h3>
          <Badge tone="blue">{province}</Badge>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
          {trade && <Badge>{trade}</Badge>}
          <span className="truncate">{location}</span>
        </div>

        <div className="mt-1 grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
            <p className="text-[11px] text-slate-500">Posted</p>
            <p className="text-sm font-semibold text-slate-900">
              {postedAt ? new Date(postedAt).toLocaleDateString() : "—"}
            </p>
          </div>
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-2">
            <p className="text-[11px] text-slate-500">Location</p>
            <p className="text-sm font-semibold text-slate-900">
              {province || "—"}
            </p>
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <Button href={`mailto:aumthakar@maoqdigital.com?subject=${subject}&body=${body}`}>
            Email resume
          </Button>
          <GhostButton href="mailto:aumthakar@maoqdigital.com">Contact</GhostButton>
        </div>
      </div>
    </div>
  );
};

/* --------------- Page ---------------- */

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // simple filters
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");
  const [prov, setProv] = useState("");

  // ---- Appwrite config (if you use it) ----
  const databaseId = process.env.REACT_APP_DATABASE_ID;
  const jobsCollectionId = process.env.REACT_APP_JOBS_COLLECTION_ID;

  useEffect(() => {
    let on = true;
    async function go() {
      setLoading(true);
      try {
        // If you are NOT ready with Appwrite, comment this block and use the fallback below.
        const res = await databases.listDocuments(databaseId, jobsCollectionId, [
          Query.equal("isPublic", true),
        ]);
        if (!on) return;

        const docs = (res?.documents || []).map((d) => ({
          id: d.$id,
          title: d.title || "Role",
          trade: d.trade || "",
          location: d.location || "",
          province: d.province || "",
          postedAt: d.postedAt || d.$createdAt,
        }));

        // newest first
        docs.sort((a, b) => (Date.parse(b.postedAt || "") || 0) - (Date.parse(a.postedAt || "") || 0));
        setJobs(docs);
      } catch (e) {
        // ---- Fallback dummy data (remove after Appwrite works) ----
        const dummy = [
          { id: "1", title: "Analyst - Real Estate", trade: "Advisory", location: "Toronto", province: "ON", postedAt: "2025-09-15" },
          { id: "2", title: "Associate - Capital", trade: "Investments", location: "Vancouver", province: "BC", postedAt: "2025-09-10" },
          { id: "3", title: "Coordinator - Listings", trade: "Operations", location: "Calgary", province: "AB", postedAt: "2025-09-05" },
        ];
        setJobs(dummy);
      } finally {
        if (on) setLoading(false);
      }
    }
    go();
    return () => { on = false; };
  }, [databaseId, jobsCollectionId]);

  const reset = () => {
    setQ("");
    setLoc("");
    setProv("");
  };

  const filtered = useMemo(() => {
    let list = jobs.slice();
    if (q.trim()) {
      const k = q.trim().toLowerCase();
      list = list.filter(
        (j) =>
          j.title.toLowerCase().includes(k) ||
          j.trade.toLowerCase().includes(k) ||
          j.location.toLowerCase().includes(k)
      );
    }
    if (loc) list = list.filter((j) => j.location === loc);
    if (prov) list = list.filter((j) => j.province === prov);
    return list;
  }, [jobs, q, loc, prov]);

  const allLocations = Array.from(new Set(jobs.map((j) => j.location).filter(Boolean)));
  const allProvinces = Array.from(new Set(jobs.map((j) => j.province).filter(Boolean)));

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>Careers — Real Estate Advisory | Maoq Digital Inc.</title>
        <meta
          name="description"
          content="Join a Canada-first real estate advisory. Email your resume to aumthakar@maoqdigital.com for current and future openings."
        />
        <link rel="canonical" href="https://www.maoqdigital.com/jobs" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* HERO (white site, subtle branded block) */}
      <section className="relative">
        <SectionWrap>
          <div className="py-14">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-10 shadow-sm">
              <div className="flex flex-col items-center text-center gap-4">
                <h1 className="heading-serif text-3xl sm:text-4xl font-extrabold text-slate-900">
                  Careers at Maoq Digital Inc.
                </h1>
                <p className="max-w-2xl text-sm sm:text-base text-slate-700">
                  Canada-first real estate advisory. If a role fits, email your resume — we keep things simple.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button href="mailto:aumthakar@maoqdigital.com">Email resume</Button>
                  <GhostButton href="/listings">Browse listings</GhostButton>
                </div>
                <div className="mt-2 flex flex-wrap justify-center gap-2">
                  <Badge>Hotels</Badge>
                  <Badge>Plazas</Badge>
                  <Badge>Land</Badge>
                  <Badge tone="green">Canada First</Badge>
                </div>
              </div>
            </div>
          </div>
        </SectionWrap>
      </section>

      {/* FILTER BAR */}
      <section>
        <SectionWrap>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm -mt-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search title, trade, or city…"
                className="h-11 rounded-xl border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={loc}
                onChange={(e) => setLoc(e.target.value)}
                className="h-11 rounded-xl border border-slate-300 px-3 text-sm"
              >
                <option value="">All locations</option>
                {allLocations.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              <select
                value={prov}
                onChange={(e) => setProv(e.target.value)}
                className="h-11 rounded-xl border border-slate-300 px-3 text-sm"
              >
                <option value="">All provinces</option>
                {allProvinces.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              <button
                onClick={reset}
                className="h-11 rounded-xl border border-slate-300 px-4 text-sm font-semibold hover:bg-slate-50"
              >
                Reset
              </button>
            </div>
          </div>
        </SectionWrap>
      </section>

      {/* JOB GRID */}
      <section>
        <SectionWrap>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-60 rounded-2xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-700">
              No open roles match your filters. Email your resume anyway —
              we often hire for upcoming needs.
              <div className="mt-3">
                <Button href="mailto:aumthakar@maoqdigital.com">Email resume</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((job) => (
                <JobCard key={job.id} job={job} />
              ))}
            </div>
          )}

          {/* Bottom CTA */}
          <div className="my-12">
            <div className="rounded-3xl border border-slate-200 bg-gradient-to-r from-blue-50 to-blue-100 p-8 text-center shadow-sm">
              <h3 className="heading-serif text-xl sm:text-2xl font-bold text-slate-900">
                Don’t see a perfect fit?
              </h3>
              <p className="mt-2 text-sm sm:text-base text-slate-800">
                Send your resume to <span className="font-semibold">aumthakar@maoqdigital.com</span>. We’ll reach out when there’s a match.
              </p>
              <div className="mt-4">
                <Button href="mailto:aumthakar@maoqdigital.com">Email resume</Button>
              </div>
            </div>
          </div>
        </SectionWrap>
      </section>

      {/* Spacer before footer */}
      <div className="pb-12" />
    </div>
  );
};

export default Jobs;
